import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Save, Upload, Palette } from "lucide-react";
import { ThemePicker } from "@/components/TemplatePicker";
import { FONT_LIBRARY, RADIUS_MAP, getSurfaceStyle } from "@/lib/templateSets";

const FONTS = FONT_LIBRARY.map((f) => f.name);

const RADIUS_OPTIONS = [
  { value: "rounded-none", label: "Eckig" },
  { value: "rounded-md", label: "Sanft" },
  { value: "rounded-xl", label: "Weich" },
  { value: "rounded-2xl", label: "Sehr weich" },
  { value: "rounded-3xl", label: "Pillow" },
  { value: "rounded-full", label: "Pille" },
];

const SURFACE_OPTIONS = [
  { value: "flat", label: "Flach", hint: "Kein Rand, kein Schatten." },
  { value: "bordered", label: "Mit Rand", hint: "Dezenter Rand, kein Schatten." },
  { value: "elevated", label: "Erhöht", hint: "Schwebend mit Soft-Shadow." },
  { value: "outlined-strong", label: "Stark umrandet", hint: "Klare 2px Outline (brutalist)." },
  { value: "glass", label: "Glas", hint: "Frosted Glass Backdrop-Blur." },
];

const PADDING_OPTIONS = [
  { value: "compact", label: "Kompakt" },
  { value: "comfortable", label: "Komfortabel" },
  { value: "spacious", label: "Luftig" },
];

export default function ThemeStudio() {
  const [theme, setTheme] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [themePickerOpen, setThemePickerOpen] = useState(false);

  useEffect(() => { api.get("/cms/theme").then(({ data }) => setTheme(data || {})); }, []);
  if (!theme) return <div className="p-8">Lade...</div>;

  const set = (k, v) => setTheme({ ...theme, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { id, _id, salon_id, updated_at, ...payload } = theme;
      await api.put("/cms/theme", payload);
    } finally { setSaving(false); }
  };

  const applyPreset = async (preset) => {
    const next = { ...theme, ...preset.theme };
    setTheme(next);
    try {
      const { id, _id, salon_id, updated_at, ...payload } = next;
      await api.put("/cms/theme", payload);
    } catch (_e) { /* user will see local state; manual save still works */ }
  };

  const upload = async (e, key) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(key, data.url);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-8 lg:p-12 mv-fade-in max-w-6xl">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="mv-label mb-2">Design System</p>
          <h1 className="font-display text-4xl">Theme Studio</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="open-theme-picker" type="button" onClick={() => setThemePickerOpen(true)} className="mv-btn-ghost">
            <Palette className="w-4 h-4" strokeWidth={1.5} /> Theme-Bibliothek
          </button>
          <button data-testid="save-theme-btn" onClick={save} disabled={saving} className="mv-btn"><Save className="w-4 h-4" /> {saving ? "..." : "Speichern"}</button>
        </div>
      </div>

      <ThemePicker open={themePickerOpen} onClose={() => setThemePickerOpen(false)} onApply={applyPreset} currentTheme={theme} />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="mv-card p-6">
          <p className="mv-label mb-4">Markenelemente</p>
          <label className="block text-xs font-medium mb-1.5">Logo</label>
          <div className="flex gap-3 items-center mb-4">
            {theme.logo_url && <img src={theme.logo_url} alt="" className="h-12 object-contain border rounded" style={{ borderColor: "var(--mv-border)" }} />}
            <label className="mv-btn-ghost text-xs cursor-pointer">
              <Upload className="w-3 h-3" /> {busy ? "..." : "Hochladen"}
              <input data-testid="upload-logo" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "logo_url")} />
            </label>
          </div>
          <label className="block text-xs font-medium mb-1.5">Favicon</label>
          <div className="flex gap-3 items-center">
            {theme.favicon_url && <img src={theme.favicon_url} alt="" className="h-8 w-8 object-contain border rounded" style={{ borderColor: "var(--mv-border)" }} />}
            <label className="mv-btn-ghost text-xs cursor-pointer">
              <Upload className="w-3 h-3" /> Hochladen
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "favicon_url")} />
            </label>
          </div>
        </div>

        <div className="mv-card p-6">
          <p className="mv-label mb-4">Farben</p>
          {[
            ["primary_color", "Primärfarbe"],
            ["secondary_color", "Sekundärfarbe"],
            ["accent_color", "Akzentfarbe (optional)"],
            ["text_color", "Textfarbe"],
            ["bg_color", "Hintergrund"],
            ["booking_card_bg", "Public Karten: Fläche"],
            ["booking_card_text_color", "Public Karten: Schrift"],
          ].map(([k, l]) => (
            <div key={k} className="flex items-center gap-3 mb-3">
              <input data-testid={`color-${k}`} type="color" value={theme[k] || "#000000"} onChange={(e) => set(k, e.target.value)} className="w-12 h-10 rounded border cursor-pointer" />
              <input className="mv-input text-sm flex-1" value={theme[k] || ""} onChange={(e) => set(k, e.target.value)} />
              <span className="text-xs w-32" style={{ color: "var(--mv-text-muted)" }}>{l}</span>
            </div>
          ))}
          <p className="text-[11px] mt-1" style={{ color: "var(--mv-text-muted)" }}>
            Gilt für die Buchungs-Karte (<code>/booking</code>), Kunden-Login (<code>/account</code>) und alle Public-Form-Karten. Leer lassen → Karten bleiben weiß mit dunklem Text.
          </p>
        </div>

        <div className="mv-card p-6 lg:col-span-2">
          <p className="mv-label mb-4">Tracking (DSGVO-konform)</p>
          <p className="text-xs mb-4" style={{ color: "var(--mv-text-muted)" }}>
            Tracking-Skripte laden erst <strong>nach Zustimmung</strong> durch deine Besucher im Cookie-Banner.
            GA4 wird unter &bdquo;Statistik&ldquo;, Meta Pixel unter &bdquo;Marketing&ldquo; eingeordnet. Leer lassen = kein Tracking.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1.5">Google Analytics 4 ID</label>
              <input
                data-testid="ga-id-input"
                className="mv-input text-sm font-mono"
                placeholder="G-XXXXXXXXXX"
                value={theme.ga_measurement_id || ""}
                onChange={(e) => set("ga_measurement_id", e.target.value.trim())}
              />
              <p className="text-[11px] mt-1" style={{ color: "var(--mv-text-muted)" }}>
                Aus deinem GA4-Property (Verwaltung → Datenströme).
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Meta-Pixel ID</label>
              <input
                data-testid="meta-pixel-input"
                className="mv-input text-sm font-mono"
                placeholder="z.B. 1234567890123456"
                value={theme.meta_pixel_id || ""}
                onChange={(e) => set("meta_pixel_id", e.target.value.trim())}
              />
              <p className="text-[11px] mt-1" style={{ color: "var(--mv-text-muted)" }}>
                Aus Meta Business Suite → Events Manager.
              </p>
            </div>
          </div>
        </div>

        <div className="mv-card p-6">
          <p className="mv-label mb-4">Schrift</p>
          <label className="block text-xs font-medium mb-1.5">Überschriften</label>
          <select data-testid="heading-font-select" className="mv-input text-sm mb-2" value={theme.heading_font || ""} onChange={(e) => set("heading_font", e.target.value)} style={{ fontFamily: `'${theme.heading_font}', serif` }}>
            {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: `'${f}', serif` }}>{f}</option>)}
          </select>
          <p className="text-2xl mb-4" style={{ fontFamily: `'${theme.heading_font}', serif` }}>Aa — Beispieltext</p>
          <label className="block text-xs font-medium mb-1.5">Fließtext</label>
          <select className="mv-input text-sm mb-2" value={theme.body_font || ""} onChange={(e) => set("body_font", e.target.value)} style={{ fontFamily: `'${theme.body_font}', sans-serif` }}>
            {FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: `'${f}', sans-serif` }}>{f}</option>)}
          </select>
          <p className="text-sm" style={{ fontFamily: `'${theme.body_font}', sans-serif` }}>Wir verbinden Handwerk und Hingabe — eine kurze Beispielzeile zum Vergleich der Lesbarkeit.</p>
        </div>

        <div className="mv-card p-6">
          <p className="mv-label mb-4">Eckenrundung & Flächen</p>
          <label className="block text-xs font-medium mb-1.5">Buttons</label>
          <select className="mv-input text-sm mb-3" data-testid="button-radius-select" value={theme.button_radius || "rounded-full"} onChange={(e) => set("button_radius", e.target.value)}>
            {RADIUS_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <label className="block text-xs font-medium mb-1.5">Blöcke (Hero, Galerien, Sektionen)</label>
          <select className="mv-input text-sm mb-3" data-testid="block-radius-select" value={theme.block_radius || "rounded-2xl"} onChange={(e) => set("block_radius", e.target.value)}>
            {RADIUS_OPTIONS.filter((r) => r.value !== "rounded-full").map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <label className="block text-xs font-medium mb-1.5">Karten (Testimonials, FAQ, Team)</label>
          <select className="mv-input text-sm mb-3" data-testid="card-radius-select" value={theme.card_radius || "rounded-xl"} onChange={(e) => set("card_radius", e.target.value)}>
            {RADIUS_OPTIONS.filter((r) => r.value !== "rounded-full").map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <label className="block text-xs font-medium mb-1.5">Karten-Stil</label>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {SURFACE_OPTIONS.map((s) => (
              <button key={s.value} type="button" onClick={() => set("surface_style", s.value)} data-testid={`surface-${s.value}`} className="p-3 text-left text-xs border" style={{ borderColor: theme.surface_style === s.value ? "var(--mv-primary)" : "var(--mv-border)", background: theme.surface_style === s.value ? "var(--mv-surface)" : "transparent", borderRadius: 10 }}>
                <p className="font-medium">{s.label}</p>
                <p className="opacity-70 text-[10px] mt-0.5">{s.hint}</p>
              </button>
            ))}
          </div>
          <label className="block text-xs font-medium mb-1.5">Sektions-Abstände</label>
          <select className="mv-input text-sm" data-testid="section-padding-select" value={theme.section_padding || "comfortable"} onChange={(e) => set("section_padding", e.target.value)}>
            {PADDING_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>

        <div className="mv-card p-6 lg:col-span-2">
          <p className="mv-label mb-4">Live-Vorschau</p>
          <div className="p-8" style={{ background: theme.bg_color, color: theme.text_color, borderRadius: RADIUS_MAP[theme.block_radius || "rounded-2xl"] }}>
            <p className="text-xs uppercase tracking-wider mb-3 opacity-70" style={{ color: theme.accent_color || theme.primary_color, fontFamily: `'${theme.body_font}', sans-serif`, letterSpacing: "0.18em" }}>Vorschau</p>
            <h3 style={{ fontFamily: `'${theme.heading_font}', serif` }} className="text-4xl md:text-5xl mb-3 leading-tight">Aa — {theme.heading_font || "Schrift"}</h3>
            <p style={{ fontFamily: `'${theme.body_font}', sans-serif` }} className="text-base mb-6 opacity-85 max-w-xl">Premium Styling und persönliche Beratung. So fühlt sich deine Webseite mit den aktuellen Theme-Einstellungen an.</p>
            <div className="flex gap-3 mb-6">
              <button className="px-6 py-3 text-white text-sm font-medium" style={{ background: theme.primary_color, borderRadius: RADIUS_MAP[theme.button_radius || "rounded-full"] }}>Termin buchen</button>
              <button className="px-6 py-3 text-sm font-medium border" style={{ borderColor: theme.text_color, color: theme.text_color, borderRadius: RADIUS_MAP[theme.button_radius || "rounded-full"] }}>Mehr erfahren</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-5" style={getSurfaceStyle(theme)}>
                <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: theme.primary_color }}>Karte</p>
                <p style={{ fontFamily: `'${theme.heading_font}', serif` }} className="text-2xl mb-2">Karten-Stil</p>
                <p className="text-sm opacity-80" style={{ fontFamily: `'${theme.body_font}', sans-serif` }}>So sehen Testimonials, FAQ und Team-Karten aus.</p>
              </div>
              <div className="p-5" style={getSurfaceStyle(theme)}>
                <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: theme.primary_color }}>Karte</p>
                <p style={{ fontFamily: `'${theme.heading_font}', serif` }} className="text-2xl mb-2">Zweite Karte</p>
                <p className="text-sm opacity-80" style={{ fontFamily: `'${theme.body_font}', sans-serif` }}>Mit aktiviertem Theme-Style.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
